import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from './components/Header';
import Footer from './components/Footer';
import PageLoading from './components/Loading';
import { ThemeProvider } from './contexts/ThemeContext';
import authService from './services/authService';

// Lazy load pages for better performance
const Home = lazy(() => import('./pages/Home'));
const Services = lazy(() => import('./pages/Services'));
const About = lazy(() => import('./pages/About'));
const Careers = lazy(() => import('./pages/Careers'));
const Contact = lazy(() => import('./pages/Contact'));

// Blog pages
const BlogList = lazy(() => import('./pages/BlogList'));
const BlogPost = lazy(() => import('./pages/BlogPost'));
const BlogCategory = lazy(() => import('./pages/BlogCategory'));
const BlogTag = lazy(() => import('./pages/BlogTag'));

// Admin pages
const Login = lazy(() => import('./pages/admin/Login'));
const BlogAdmin = lazy(() => import('./pages/admin/BlogAdmin'));

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  if (!authService.isAuthenticated()) {
    return <Navigate to="/admin/login" />;
  }
  return children;
};

// Admin Only Route Component
const AdminRoute = ({ children }) => {
  if (!authService.isAdmin()) {
    return <Navigate to="/admin/login" />;
  }
  return children;
};

function App() {
  return (
    <ThemeProvider>
      <Router>
        <div className="min-h-screen font-inter bg-gray-50 dark:bg-dark-950 text-gray-900 dark:text-white transition-colors duration-300">
        <Helmet>
          <meta charSet="utf-8" />
          <title>NeoVam Technologies — AI for Humanity</title>
          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <meta name="theme-color" content="#0c1221" />
          <link rel="icon" href="/favicon.ico" />
          <meta name="description" content="NeoVam Technologies builds AI, Cloud and Fintech solutions to accelerate digital transformation across Africa." />
          <meta property="og:site_name" content="NeoVam Technologies" />
          <meta property="og:url" content="https://neovam.com" />
          <meta property="og:type" content="website" />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:site" content="@neovamtech" />
          
          {/* Structured Data for SEO */}
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Organization",
              "name": "NeoVam Technologies",
              "url": "https://neovam.com",
              "logo": "https://neovam.com/logo.png",
              "description": "AI, Cloud and Fintech solutions for digital transformation across Africa",
              "sameAs": [
                "https://twitter.com/neovamtech",
                "https://linkedin.com/company/neovam"
              ],
              "contactPoint": {
                "@type": "ContactPoint",
                "telephone": "",
                "contactType": "customer service",
                "availableLanguage": "English"
              }
            })}
          </script>
        </Helmet>

        <Header />

        <main className="pt-0">
          <Suspense fallback={<PageLoading />}>
            <Routes>
              {/* ========== MAIN WEBSITE ROUTES ========== */}
              <Route path="/" element={<Home />} />
              <Route path="/services" element={<Services />} />
              <Route path="/about" element={<About />} />
              <Route path="/careers" element={<Careers />} />
              <Route path="/contact" element={<Contact />} />
              
              {/* ========== BLOG ROUTES (PUBLIC) ========== */}
              <Route path="/blog" element={<BlogList />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/blog/category/:category" element={<BlogCategory />} />
              <Route path="/blog/tag/:tag" element={<BlogTag />} />
              <Route path="/blog/search/:query" element={<BlogList />} />
              
              {/* ========== ADMIN ROUTES ========== */}
              {/* Public Login Route */}
              <Route path="/admin/login" element={<Login />} />
              
              {/* Protected Admin Routes */}
              <Route 
                path="/admin" 
                element={
                  <AdminRoute>
                    <Navigate to="/admin/blog" replace />
                  </AdminRoute>
                } 
              />
              
              <Route 
                path="/admin/blog" 
                element={
                  <AdminRoute>
                    <BlogAdmin />
                  </AdminRoute>
                } 
              />
              
              {/* Remove AdminPanel route since you deleted it */}
              {/* <Route path="/admin/panel" element={<AdminRoute><AdminPanel /></AdminRoute>} */}
              
              <Route 
                path="/admin/create" 
                element={
                  <AdminRoute>
                    <BlogAdmin />
                  </AdminRoute>
                } 
              />
              
              <Route 
                path="/admin/edit/:id" 
                element={
                  <AdminRoute>
                    <BlogAdmin />
                  </AdminRoute>
                } 
              />
              
              {/* ========== UTILITY ROUTES ========== */}
              {/* SITEMAP ROUTE REMOVED - Now handled by vercel.json */}
              <Route path="/rss.xml" element={<Navigate to="/api/rss.xml" />} />
              <Route path="/feed" element={<Navigate to="/api/rss.xml" />} />
              
              {/* ========== REDIRECTS (SEO FRIENDLY) ========== */}
              <Route path="/blog/page/:page" element={<BlogList />} />
              <Route path="/articles" element={<Navigate to="/blog" replace />} />
              <Route path="/news" element={<Navigate to="/blog" replace />} />
              <Route path="/insights" element={<Navigate to="/blog" replace />} />
              <Route path="/ai-blog" element={<Navigate to="/blog/category/Artificial Intelligence" replace />} />
              <Route path="/fintech-blog" element={<Navigate to="/blog/category/Fintech" replace />} />
              <Route path="/cloud-blog" element={<Navigate to="/blog/category/Cloud Computing" replace />} />
              
              {/* ========== 404 PAGE ========== */}
              <Route path="*" element={
                <div className="min-h-screen flex items-center justify-center pt-24">
                  <div className="text-center">
                    <h1 className="text-6xl font-bold text-gradient mb-4">404</h1>
                    <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">Page Not Found</h2>
                    <p className="text-gray-600 dark:text-gray-400 mb-8">The page you're looking for doesn't exist.</p>
                    <a 
                      href="/" 
                      className="inline-flex items-center px-6 py-3 bg-gradient-primary text-white font-semibold rounded-xl hover:shadow-glow hover:scale-105 transition-all duration-300"
                    >
                      Go Home
                    </a>
                  </div>
                </div>
              } />
            </Routes>
          </Suspense>
        </main>

        <Footer />
        </div>
      </Router>
    </ThemeProvider>
  );
}

export default App;